import { useMemo, useState } from "react";
import { Box, Chip, Paper, Table, TableBody, TableCell, TableHead, TableRow, Typography } from "@mui/material";
import HtmlWithLatex from "../components/HtmlWithLatex";

export default function MatchingExpressionFill({
  context,
  values = [],
  onChange,
}) {
  const [selectedBankItem, setSelectedBankItem] = useState(null);
  const slotValues = useMemo(
    () => Array.from({ length: context.slotCount }, (_, idx) => values[idx] ?? ""),
    [context.slotCount, values]
  );
  const usedItems = useMemo(
    () => new Set(slotValues.map((v) => v.trim()).filter(Boolean)),
    [slotValues]
  );

  const updateSlot = (index, value) => {
    const next = Array.from({ length: context.slotCount }, (_, idx) => slotValues[idx] ?? "");
    next[index] = value;
    onChange?.(next);
  };

  const placeInSlot = (index, item) => {
    const existingIndex = slotValues.findIndex((v) => v.trim() === item);
    if (existingIndex >= 0 && existingIndex !== index) {
      updateSlot(existingIndex, "");
    }
    updateSlot(index, item);
    setSelectedBankItem(null);
  };

  return (
    <Box sx={{ mt: 2 }}>
      {context.instructionHtml ? (
        <Box sx={{ mb: 2, lineHeight: 1.7 }}>
          <HtmlWithLatex html={context.instructionHtml} />
        </Box>
      ) : null}

      <Box sx={{ overflowX: "auto", mb: 2 }}>
        <Table size="small" sx={{ border: "2px solid #333", minWidth: 520 }}>
          <TableHead>
            <TableRow sx={{ bgcolor: "#f5f5f5" }}>
              <TableCell sx={{ fontWeight: 700, borderRight: "1px solid #333" }}>Statement</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Expression</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {context.displayRows.map((row, index) => {
              const slotIndex = context.slotStatements.indexOf(row.statement);
              const isInteractive = slotIndex >= 0;
              const slotValue = isInteractive ? slotValues[slotIndex] ?? "" : "";
              const showExample = row.isExample && row.prefilledExpression;

              return (
                <TableRow key={`${index}-${row.statement.slice(0, 24)}`}>
                  <TableCell sx={{ borderRight: "1px solid #333", verticalAlign: "top" }}>
                    <HtmlWithLatex html={row.statement} />
                  </TableCell>
                  <TableCell sx={{ verticalAlign: "middle" }}>
                    {showExample ? (
                      <Typography sx={{ fontWeight: 700, color: "primary.main" }}>{row.prefilledExpression}</Typography>
                    ) : isInteractive ? (
                      <Paper
                        variant="outlined"
                        onClick={() => {
                          if (selectedBankItem) {
                            placeInSlot(slotIndex, selectedBankItem);
                            return;
                          }
                          if (slotValue) updateSlot(slotIndex, "");
                        }}
                        sx={{
                          px: 1.5,
                          py: 1,
                          minHeight: 40,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          cursor: "pointer",
                          borderStyle: slotValue ? "solid" : "dashed",
                          borderColor: "primary.main",
                          bgcolor: selectedBankItem && !slotValue ? "primary.50" : slotValue ? "primary.50" : "background.paper",
                        }}
                      >
                        <Typography
                          variant="body2"
                          sx={{ fontWeight: 700, color: slotValue ? "primary.main" : "text.secondary", fontStyle: slotValue ? "normal" : "italic" }}
                        >
                          {slotValue || "Click to place"}
                        </Typography>
                      </Paper>
                    ) : row.prefilledExpression ? (
                      <Typography variant="body2">{row.prefilledExpression}</Typography>
                    ) : (
                      <Typography variant="body2" color="text.secondary">—</Typography>
                    )}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </Box>

      <Typography variant="body2" sx={{ fontWeight: 700, mb: 0.5 }}>Answer bank</Typography>
      <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1 }}>
        Click a value, then click a blank in the table
      </Typography>
      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
        {context.bank.map((item) => {
          const used = usedItems.has(item);
          const selected = selectedBankItem === item;
          return (
            <Chip
              key={item}
              label={item}
              clickable={!used}
              color={selected ? "primary" : "default"}
              variant={used ? "outlined" : selected ? "filled" : "outlined"}
              onClick={() => {
                if (used) return;
                setSelectedBankItem(selected ? null : item);
              }}
              sx={{ fontWeight: 700, opacity: used ? 0.45 : 1 }}
            />
          );
        })}
      </Box>
    </Box>
  );
}
